import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Check, X, Clock } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

export default function PaymentResult() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuccess = location.pathname.includes("success");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!isSuccess) { setStatus("cancel"); return; }
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) { setStatus("paid"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await refresh(); setStatus("paid"); return; }
      } catch (e) {}
      if (tries++ < 6) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [isSuccess, location.search]);

  const goToDashboard = async () => {
    await refresh();
    navigate("/dashboard");
  };

  return (
    <div data-testid="payment-result" className="max-w-lg mx-auto px-5 py-32 text-center">
      {status === "checking" && <p className="font-display text-3xl text-primary/50 animate-pulse">{t("Confirming payment...", "Confirmation du paiement...")}</p>}
      {status === "paid" && (
        <>
          <div className="w-20 h-20 rounded-full bg-secondary/15 text-secondary flex items-center justify-center mx-auto mb-6"><Check size={40} /></div>
          <h1 className="font-display text-4xl font-medium text-primary">{t("You're in!", "Vous y êtes !")}</h1>
          <p className="text-foreground/70 mt-3">{t("Your membership is confirmed. Welcome to the club.", "Votre adhésion est confirmée. Bienvenue au club.")}</p>
          <button onClick={goToDashboard} data-testid="payment-go-dashboard" className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3.5 font-semibold hover:bg-secondary transition-colors">{t("Go to dashboard", "Tableau de bord")}</button>
        </>
      )}
      {status === "pending" && (
        <>
          <div className="w-20 h-20 rounded-full bg-accent/30 text-secondary flex items-center justify-center mx-auto mb-6"><Clock size={38} /></div>
          <h1 className="font-display text-4xl font-medium text-primary">{t("Almost there…", "Presque terminé…")}</h1>
          <p className="text-foreground/70 mt-3">{t("We're still confirming your payment with Stripe. This can take a moment — we'll email you as soon as your membership is active. You can safely head to your dashboard.", "Nous confirmons encore votre paiement auprès de Stripe. Cela peut prendre un moment — nous vous enverrons un courriel dès que votre adhésion sera active.")}</p>
          <button onClick={goToDashboard} data-testid="payment-go-dashboard-pending" className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3.5 font-semibold hover:bg-secondary transition-colors">{t("Go to dashboard", "Tableau de bord")}</button>
        </>
      )}
      {status === "cancel" && (
        <>
          <div className="w-20 h-20 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-6"><X size={40} /></div>
          <h1 className="font-display text-4xl font-medium text-primary">{t("Payment cancelled", "Paiement annulé")}</h1>
          <p className="text-foreground/70 mt-3">{t("No worries — you can join anytime.", "Pas de souci — vous pouvez adhérer à tout moment.")}</p>
          <Link to="/join" className="inline-block mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3.5 font-semibold hover:bg-secondary transition-colors">{t("Try again", "Réessayer")}</Link>
        </>
      )}
    </div>
  );
}
